import { tagDisplayName } from '../../../utils/helpers';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Skeleton } from 'primereact/skeleton';

import axiosInstance from '../../../config/Axios';
import { useCafeMedia } from '../../../queries/cafeWizard';
import { useCompanyPlaylists } from '../../../queries/playlists';
import { setRerender } from '../../../redux/Slices/scrollSlice';
import UploadMedia from '../../Media/components/UploadPopup/UploadMedia';
import YouTubeSearchDialog from '../../Links/YouTubeSearchDialog';

const PAGE_SIZE = 20;

const flattenPlaylistPages = (q) => (q.data?.pages || []).flatMap((p) => p.items || []);

// Ported verbatim from ShowPage.jsx's fmtNumber (itself ported from
// LoyaltyAnalytics.jsx's `fmt`) — Arabic-indic digits via 'ar-EG', not raw
// JS number-to-string. No shared export exists for this in the codebase yet;
// duplicated locally the same way every other call site does.
const fmtNumber = (n, lng) => {
  try { return new Intl.NumberFormat(lng === 'ar' ? 'ar-EG' : 'en-US').format(n); }
  catch { return String(n); }
};

/**
 * Professional media picker used by the café wizard steps.
 *
 * ⚠️ PORTED FROM .claude/fastcreate-prototype.html's stepPicker() — markup
 * and CSS classes (fc-*), not rebuilt. Real data throughout: the prototype's
 * PLAYLISTS/MPLAYLISTS/LIB/TRACKS were fixtures for the demo; this fetches
 * the company's actual library and actual playlists.
 *
 * New this round:
 *   - Files/Lists tabs — Lists (your existing playlists) only renders when
 *     useCompanyPlaylists() actually has rows; selecting a playlist expands
 *     its own media into this run's selection (what the wizard PRODUCES is
 *     unchanged — still a flat media_ids/music_media_ids list, same as
 *     picking files one at a time, just faster).
 *   - Zoom (⤢) on an image cell opens a larger preview.
 *   - ▶/⏸ preview per audio track, real <audio> playback of the track's own
 *     path — not a decorative button.
 *
 *  - Search (instant, by filename) with a clear button
 *  - Filter tabs (visual family): All · Images · Videos
 *  - Multi-select with checkbox overlay + play-order badge
 *  - Selected chips + count, Select-all (filtered) / Clear
 *  - Drag-and-drop upload (new files appear at the TOP immediately)
 *  - Client-side pagination (20/page)
 *
 * Props:
 *   family      : 'visual' (photos/videos) | 'music' (audio)
 *   value       : ordered array of selected media ids
 *   onChange    : (ids) => void
 *   allowUpload : show the drag-drop upload zone (default true)
 */
export default function MediaPicker({ family = 'visual', value = [], onChange, allowUpload = true }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.split('-')[0] || 'en';
  const dispatch = useDispatch();
  const { company } = useSelector((s) => s.global.companyDetails) || {};

  const isMusic = family === 'music';

  const visualQ  = useCafeMedia('visual',  !isMusic);
  const musicQ   = useCafeMedia('music',   isMusic);
  const youtubeQ = useCafeMedia('youtube', true);

  const media = useMemo(() => {
    const yt = youtubeQ.data || [];
    const base = isMusic ? (musicQ.data || []) : (visualQ.data || []);
    return [...base, ...yt].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [isMusic, musicQ.data, visualQ.data, youtubeQ.data]);
  const isLoading = (isMusic ? musicQ.isLoading : visualQ.isLoading) || youtubeQ.isLoading;
  const isError = (isMusic ? musicQ.isError : visualQ.isError) || youtubeQ.isError;
  const refetch = () => { youtubeQ.refetch(); if (isMusic) musicQ.refetch(); else visualQ.refetch(); };

  // Unfiltered existence probe — decides whether the Files/Lists toggle shows
  // at all. Kept separate from the (searched, paginated) query below so that
  // typing a search with no matches doesn't make the toggle itself vanish.
  const playlistsExistQ = useCompanyPlaylists({ per_page: 1 });
  const hasAnyPlaylists = (playlistsExistQ.data?.pages?.[0]?.totalItems ?? 0) > 0;

  const [listSearch, setListSearch] = useState('');
  const playlistsQ = useCompanyPlaylists({ per_page: 20, search: listSearch.trim() || undefined });
  const playlists = flattenPlaylistPages(playlistsQ);
  const playlistsTotal = playlistsQ.data?.pages?.[0]?.totalItems ?? playlists.length;

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(isMusic ? 'AUDIO' : 'ALL');
  const [first, setFirst] = useState(0);
  const [ytOpen, setYtOpen] = useState(false);
  const [src, setSrc] = useState('files'); // 'files' | 'lists'
  const [expanding, setExpanding] = useState(null); // playlist id currently being expanded
  const [zoomId, setZoomId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  // ⚠️ IDENTITY, NOT CONTENT — see expandPlaylist/collapsePlaylist below for
  // the full design. Both live in THIS component's own state, same as
  // `src`/`tab`/`expanding` above: switching the Files/Lists toggle (`src`)
  // or any other tab is a conditional render branch inside this same
  // component instance, never an unmount, so React preserves both across
  // that switch — proven by the "shared file" / "hand-picked file" tests
  // below, which switch tabs mid-scenario. The one case this does NOT
  // survive is the wizard navigating AWAY from this step and back
  // (MediaPickStep/MusicPickStep are conditionally rendered by stepIndex in
  // CafeVibesWizard.jsx, so MediaPicker itself unmounts) — both reset to
  // empty on remount. That degrades safely, not back to the original bug:
  // an empty Map marks nothing, so no playlist can come back mis-marked
  // from stale content-overlap; the only cost is a previously-marked
  // playlist showing unmarked again even though its files are still in
  // `value`, and a stale hand-pick losing its protection from a NEW
  // playlist selection made after the remount. Lifting this to wizard-level
  // state would close that gap but is a bigger change than the approved
  // design called for.
  const [selectedPlaylists, setSelectedPlaylists] = useState(() => new Map()); // playlistId -> full media-id membership, as of the click that selected it
  const [handPickedIds, setHandPickedIds] = useState(() => new Set()); // ids added by a raw per-file toggle (Files tab, Select All, YouTube add) — never owned by a playlist snapshot, so a playlist deselect can never reclaim them

  const onYoutubeAdded = (created = []) => {
    const ids = created.map((m) => m?.id).filter(Boolean);
    const add = ids.filter((id) => !value.includes(id));
    if (add.length) {
      onChange([...value, ...add]);
      setHandPickedIds((prev) => {
        const next = new Set(prev);
        add.forEach((id) => next.add(id));
        return next;
      });
    }
    youtubeQ.refetch();
    setTab('YOUTUBE');
    setFirst(0);
    setYtOpen(false);
  };

  const rerender = useSelector((s) => s.scroll?.rerender);
  useEffect(() => {
    if (!rerender) return;
    refetch();
    dispatch(setRerender(false));
    setFirst(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerender]);

  // Stop any playing preview when the step unmounts or the family changes.
  useEffect(() => () => { audioRef.current?.pause(); }, []);

  const nameOf = (m) => (m.title || m.file_name || '').toString();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return media
      .filter((m) => (tab === 'ALL' ? true : m.type === tab))
      .filter((m) => (q ? nameOf(m).toLowerCase().includes(q) : true));
  }, [media, tab, search]);

  useEffect(() => { setFirst(0); }, [search, tab, family]);

  const paged = filtered.slice(first, first + PAGE_SIZE);
  const byId = useMemo(() => new Map(media.map((m) => [m.id, m])), [media]);

  // ⚠️ TRACKS PROVENANCE, NOT JUST `value`. A raw per-file toggle (this
  // function) is the definition of "hand-picked" — see handPickedIds above.
  // Toggling OFF also drops the id from handPickedIds: once gone from
  // `value` there's nothing left to protect, and re-adding it later (by
  // hand or via a playlist) should start from a clean slate.
  const toggle = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
      setHandPickedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } else {
      onChange([...value, id]);
      setHandPickedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
    }
  };
  const selectAllFiltered = () => {
    const add = filtered.map((m) => m.id).filter((id) => !value.includes(id));
    if (!add.length) return;
    onChange([...value, ...add]);
    setHandPickedIds((prev) => {
      const next = new Set(prev);
      add.forEach((id) => next.add(id));
      return next;
    });
  };
  // Clears everything, including provenance — an empty `value` has no
  // playlists or hand-picks left to track.
  const clearAll = () => {
    onChange([]);
    setSelectedPlaylists(new Map());
    setHandPickedIds(new Set());
  };

  const togglePlay = (m) => {
    if (playingId === m.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    audioRef.current?.pause();
    const el = new Audio(m.path);
    el.onended = () => setPlayingId(null);
    el.play().catch(() => setPlayingId(null));
    audioRef.current = el;
    setPlayingId(m.id);
  };

  // ⚠️ EXPANDS INTO THE SAME FLAT SELECTION — picking a playlist is a
  // shortcut for picking its files one at a time, not a new "reference a
  // playlist" capability. What the wizard produces does not change.
  const [expandError, setExpandError] = useState(null);

  // ⚠️ IDENTITY, NOT A CONTENT SUBSET-CHECK — this used to be `ids.length > 0
  // && ids.every(id => value.includes(id))`, i.e. "does `value` currently
  // hold every one of this playlist's media ids". That cannot tell two
  // playlists with byte-identical content apart: selecting ONE marked BOTH,
  // because `value` satisfied both playlists' subset check at once (the
  // root cause traced in fast_create_lists_tab_pagination_and_selection_fix
  // — measured live, not hypothetical). A playlist is marked now only if
  // THIS row was actually clicked — recorded in selectedPlaylists — never
  // inferred from what `value` happens to contain.
  const isPlaylistSelected = (pl) => selectedPlaylists.has(pl.id);

  // ⚠️ REMOVES THIS PLAYLIST'S OWN RECORDED SNAPSHOT, not a re-derived
  // content check — `selectedPlaylists.get(playlist.id)` is exactly what
  // expandPlaylist fetched and added when this row was clicked, so there is
  // nothing to race or diverge from.
  //
  // An id survives removal for either of two reasons:
  //   - it's still needed by another playlist CURRENTLY in selectedPlaylists
  //     (the "A and B share a file" case — reads their own recorded
  //     snapshots, not a live re-scan of `playlists`, so it can't be fooled
  //     by two identical-content rows the way the old subset-check was), or
  //   - it's in handPickedIds — chosen individually (Files tab / Select All
  //     / YouTube), tracked from the moment it was added regardless of
  //     whether it also happens to be a real member of THIS playlist. This
  //     is what makes a hand-picked file survive even when it was picked
  //     BEFORE selecting a playlist that also contains it: the file was
  //     never inferred from overlap, so this playlist's own snapshot
  //     "containing" it is irrelevant — it's protected by construction, not
  //     because nothing else happened to claim it.
  const collapsePlaylist = (playlist) => {
    const ids = selectedPlaylists.get(playlist.id) || [];
    const stillNeeded = new Set(handPickedIds);
    selectedPlaylists.forEach((otherIds, otherId) => {
      if (otherId === playlist.id) return;
      otherIds.forEach((id) => stillNeeded.add(id));
    });
    const toRemove = ids.filter((id) => !stillNeeded.has(id));
    if (toRemove.length) onChange(value.filter((id) => !toRemove.includes(id)));
    setSelectedPlaylists((prev) => {
      if (!prev.has(playlist.id)) return prev;
      const next = new Map(prev);
      next.delete(playlist.id);
      return next;
    });
  };

  // ⚠️ LAYER-AGNOSTIC ON PURPOSE. This used to hardcode /MAIN/media — but a
  // real café's own playlists are tagged by whatever ambient layer was
  // active when they were built, and plenty are PLAYGROUND (any "photos +
  // music" pair the wizard's own layer-swap produces, e.g. "الصباح · صور").
  // The MAIN-filtered read returned 200 with zero rows for those — no
  // error, nothing added, indistinguishable from "the row is dead". Fixed
  // server-side (GET /playlist/{id}/media, no layer filter) rather than
  // guessing a layer client-side.
  const expandPlaylist = async (playlistId) => {
    setExpanding(playlistId);
    setExpandError(null);
    try {
      const { data } = await axiosInstance.get(`/playlist/${playlistId}/media`);
      const rows = Array.isArray(data?.data) ? data.data : [];
      const ids = rows.map((r) => r.media_id || r.id).filter(Boolean);
      if (!ids.length) {
        setExpandError(playlistId);
        return;
      }
      // Record the FULL membership this click resolved, from the
      // authoritative fetch — not `mediaIdsOf(pl)`'s (possibly stale/partial)
      // list-row copy — so collapsePlaylist later removes exactly this.
      setSelectedPlaylists((prev) => {
        const next = new Map(prev);
        next.set(playlistId, ids);
        return next;
      });
      onChange([...value, ...ids.filter((id) => !value.includes(id))]);
    } catch {
      setExpandError(playlistId);
    } finally {
      setExpanding(null);
    }
  };

  const capacity = useMemo(() => {
    const allowed = Number(company?.allowed_media_usage);
    const used = Number(company?.media_usage);
    return Number.isFinite(allowed) && Number.isFinite(used) ? Math.max(0, allowed - used) : Number.MAX_SAFE_INTEGER;
  }, [company]);

  const zoomMedia = zoomId ? byId.get(zoomId) : null;

  return (
    <div>
      {/* Files / Lists — Lists only renders when playlists actually exist */}
      {hasAnyPlaylists && (
        <div className="fc-srcs">
          <button type="button" aria-pressed={src === 'files'} onClick={() => setSrc('files')}>
            {t('cafe_wizard.src_files')}
          </button>
          <button type="button" aria-pressed={src === 'lists'} onClick={() => setSrc('lists')}>
            {t('cafe_wizard.src_lists')}
          </button>
        </div>
      )}

      {src === 'lists' && hasAnyPlaylists ? (
        <>
          <div className="fc-sel" style={{ marginTop: 0, marginBottom: 10 }}>
            {t('cafe_wizard.lists_total', { n: fmtNumber(playlistsTotal, lng) })}
          </div>
          <div className="fc-tools">
            <input
              type="text" value={listSearch} onChange={(e) => setListSearch(e.target.value)}
              placeholder={t('cafe_wizard.lists_search_placeholder')}
            />
          </div>

          {playlists.length === 0 ? (
            <div className="fc-emptylib">{t('cafe_wizard.no_lists_search')}</div>
          ) : (
            <div className="fc-rowlist">
              {(() => {
                const q = listSearch.trim().toLowerCase();
                return playlists.map((pl) => {
                  const isSelected = isPlaylistSelected(pl);
                  const tags = pl.tags_list || [];
                  return (
                    <button
                      key={pl.id} type="button" className="fc-row"
                      aria-pressed={isSelected}
                      disabled={expanding === pl.id}
                      onClick={() => (isSelected ? collapsePlaylist(pl) : expandPlaylist(pl.id))}
                    >
                      <span className="ic">{isMusic ? '🎵' : '🗂️'}</span>
                      <span className="b">
                        <span className="t">{pl.name}</span><br />
                        <span className="s">
                          {/* ⚠️ WHY A ROW MATCHED: the search also matches
                              tag names (backend combines name-or-tag in one
                              clause) — when a tag, not the name, is why this
                              row is here, that tag chip is highlighted so
                              the match has a visible reason. */}
                          {tags.map((tg) => {
                            const matched = q.length > 0 && tg.name.toLowerCase().includes(q);
                            return (
                              <span key={tg.id} className={`fc-tagchip${matched ? ' matched' : ''}`}>
                                {tagDisplayName(tg, t)}
                              </span>
                            );
                          })}
                          {typeof pl.media?.length === 'number' ? ` · ${t('cafe_wizard.n_items', { count: fmtNumber(pl.media.length, lng) })}` : ''}
                        </span>
                      </span>
                      {expandError === pl.id && <span className="tag">{t('cafe_wizard.list_empty_or_failed')}</span>}
                      {expanding === pl.id && <span className="tag">…</span>}
                    </button>
                  );
                });
              })()}
            </div>
          )}

          {playlistsQ.hasNextPage && (
            <div className="d-flex justify-content-center" style={{ marginTop: 12 }}>
              <button type="button" className="fc-btn fc-btn-ghost" disabled={playlistsQ.isFetchingNextPage}
                      onClick={() => playlistsQ.fetchNextPage()}>
                {t('cafe_wizard.load_more_lists', { count: Math.min(20, Math.max(0, playlistsTotal - playlists.length)) })}
              </button>
            </div>
          )}

          <div className="fc-guide">
            <b>{t('cafe_wizard.why_lists_title')}</b> {t('cafe_wizard.why_lists_body')}
          </div>
          <div className="fc-sel">
            {value.length ? t('cafe_wizard.n_selected', { n: fmtNumber(value.length, lng) }) : t('cafe_wizard.none_selected')}
          </div>
        </>
      ) : (
        <>
          {allowUpload && (
            <div className="fc-drop-wrap">
              <UploadMedia
                capacityAvailability={capacity}
                currentFolder={null}
                disabled={capacity <= 0}
                onClose={() => { refetch(); setFirst(0); }}
              />
            </div>
          )}

          <div className="fc-tools">
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t('cafe_wizard.search_placeholder')}
            />
            {!isMusic && (
              <div className="fc-chips">
                {[['ALL', t('cafe_wizard.tab_all')], ['IMAGE', t('cafe_wizard.tab_images')], ['VIDEO', t('cafe_wizard.tab_videos')]].map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>{l}</button>
                ))}
              </div>
            )}
          </div>
          <div className="fc-chips" style={{ marginBottom: 12 }}>
            {isMusic && [['AUDIO', t('cafe_wizard.tab_my_audio')], ['YOUTUBE', t('cafe_wizard.tab_my_youtube')]].map(([k, l]) => (
              <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)}>{l}</button>
            ))}
            <button type="button" onClick={() => setYtOpen(true)} style={{ borderColor: '#ff0000', color: '#ff0000' }}>
              <i className="pi pi-youtube" /> {t('cafe_wizard.search_youtube')}
            </button>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ marginBottom: 10 }}>
            <span className="fc-sel" style={{ margin: 0, color: value.length ? 'var(--brand)' : 'var(--ink-mute)', fontWeight: value.length ? 600 : 400 }}>
              {value.length ? t('cafe_wizard.n_selected', { n: fmtNumber(value.length, lng) }) : t('cafe_wizard.none_selected')}
            </span>
            <div className="d-flex gap-2">
              <button type="button" className="fc-btn fc-btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }}
                      disabled={filtered.length === 0} onClick={selectAllFiltered}>
                {t('cafe_wizard.select_all')}
              </button>
              <button type="button" className="fc-btn fc-btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }}
                      disabled={value.length === 0} onClick={clearAll}>
                {t('cafe_wizard.clear')}
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="fc-lib">
              {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height="90px" borderRadius="7px" />)}
            </div>
          ) : isError ? (
            <div className="fc-error">{t('cafe_wizard.library_load_error')}</div>
          ) : filtered.length === 0 ? (
            <div className="fc-emptylib">
              {search ? t('cafe_wizard.no_results_search')
                : isMusic ? t('cafe_wizard.no_music_files') : t('cafe_wizard.empty_media_hint')}
            </div>
          ) : isMusic ? (
            <div className="fc-rowlist">
              {paged.map((m) => {
                const idx = value.indexOf(m.id);
                const on = idx > -1;
                return (
                  <div key={m.id} className="fc-row" aria-pressed={on} style={{ cursor: 'default' }}>
                    <button type="button" className="fc-playbtn" title={t('cafe_wizard.listen')}
                            onClick={(e) => { e.stopPropagation(); togglePlay(m); }}>
                      {playingId === m.id ? '⏸' : '▶'}
                    </button>
                    <span className="b" style={{ cursor: 'pointer' }} onClick={() => toggle(m.id)}>
                      <span className="t">{nameOf(m)}</span><br />
                      <span className="s">{playingId === m.id ? t('cafe_wizard.now_playing') : t('cafe_wizard.audio')}</span>
                    </span>
                    {on && <span className="tag free">{fmtNumber(idx + 1, lng)}</span>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="fc-lib">
              {paged.map((m) => {
                const idx = value.indexOf(m.id);
                const on = idx > -1;
                return (
                  <button key={m.id} type="button" className="fc-cell" aria-pressed={on} onClick={() => toggle(m.id)} title={nameOf(m)}>
                    <div className="th">
                      {m.thumb ? <img src={m.thumb} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        : (m.type === 'VIDEO' ? '🎬' : m.type === 'YOUTUBE' ? '▶️' : '🖼️')}
                    </div>
                    <button type="button" className="fc-zoom" title={t('cafe_wizard.zoom')}
                            onClick={(e) => { e.stopPropagation(); setZoomId(m.id); }}>⤢</button>
                    {on && <span className="ord">{fmtNumber(idx + 1, lng)}</span>}
                    <div className="cap">{nameOf(m)}<br /><span className="ty">{m.type}</span></div>
                  </button>
                );
              })}
            </div>
          )}

          {filtered.length > PAGE_SIZE && (
            <div className="d-flex justify-content-center gap-2" style={{ marginTop: 12 }}>
              <button type="button" className="fc-btn fc-btn-ghost" disabled={first === 0}
                      onClick={() => setFirst(Math.max(0, first - PAGE_SIZE))}>‹</button>
              <span className="small text-muted" style={{ alignSelf: 'center' }}>
                {t('cafe_wizard.page_report_simple', { from: fmtNumber(first + 1, lng), to: fmtNumber(Math.min(first + PAGE_SIZE, filtered.length), lng), total: fmtNumber(filtered.length, lng) })}
              </span>
              <button type="button" className="fc-btn fc-btn-ghost" disabled={first + PAGE_SIZE >= filtered.length}
                      onClick={() => setFirst(first + PAGE_SIZE)}>›</button>
            </div>
          )}

          <div className="fc-sel">
            {value.length
              ? `${fmtNumber(value.length, lng)} ${isMusic ? t('cafe_wizard.tracks_word') : t('cafe_wizard.files_word')} — ${t('cafe_wizard.in_chosen_order')}`
              : (isMusic ? t('cafe_wizard.none_selected_music') : t('cafe_wizard.none_selected'))}
          </div>
        </>
      )}

      {zoomMedia && (
        <div className="fc-ovl" role="button" tabIndex={0} onClick={() => setZoomId(null)}>
          <div className="fc-zbox" onClick={(e) => e.stopPropagation()}>
            <div className="fc-zimg">
              {zoomMedia.thumb
                ? <img src={zoomMedia.thumb} alt="" />
                : (zoomMedia.type === 'VIDEO' ? '🎬' : '🖼️')}
            </div>
            <div className="fc-zcap">{nameOf(zoomMedia)} · {zoomMedia.type}</div>
          </div>
        </div>
      )}

      <YouTubeSearchDialog visible={ytOpen} onHide={() => setYtOpen(false)} onAdded={onYoutubeAdded} />
    </div>
  );
}
